import { useLayoutEffect, useRef, useState } from 'react';
import { styled, Tooltip } from '@mui/material';
import { Grid } from '@mui/system';
import { TextCellProps } from './TextCell.props';

const PREFIX = 'TextCell';
const TEXT_PADDING = 10;
const DROPDOWN_ICON_WIDTH = 24;

const classes = {
  tooltip: `${PREFIX}-tooltip`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.tooltip}`]: {
    margin: '0px',
  },
}));

const CustomTooltip = Tooltip;

const TextCell = (props: TextCellProps) => {
  const {
    children,
    isReadOnly,
    hasValidationTooltip,
    shouldDisplayTooltip: controlledTooltip,
    textRef: externalTextRef,
  } = props;
  const internalTextRef = useRef<HTMLSpanElement>(null);
  const textRef = externalTextRef ?? internalTextRef;
  const [isTruncated, setIsTruncated] = useState(false);

  // Measure again when the rendered mode changes, even if the label is identical.
  // Keeping this state here avoids rebuilding option lists for tooltip-only updates.
  useLayoutEffect(() => {
    // MultiSelectValueViewer still supplies its own measurement and tooltip state.
    if (controlledTooltip !== undefined) {
      return;
    }
    const containerWidth = textRef.current?.closest<HTMLElement>('.select-value-viewer')?.offsetWidth ?? 0;
    const textWidth = textRef.current?.offsetWidth ?? 0;
    const textOffset = TEXT_PADDING + (isReadOnly ? 0 : DROPDOWN_ICON_WIDTH);
    setIsTruncated(containerWidth > 0 && textWidth + textOffset >= containerWidth);
  }, [children, controlledTooltip, isReadOnly, textRef]);

  const shouldDisplayTooltip = controlledTooltip ?? (isTruncated && !hasValidationTooltip);

  return (
    <StyledGrid className="text-cell" component="span">
      <Grid component="span">
        {shouldDisplayTooltip ? (
          <CustomTooltip
            classes={{
              tooltip: classes.tooltip,
            }}
            placement="bottom-end"
            title={children?.toString() ?? ''}>
            <Grid component="span">{children}</Grid>
          </CustomTooltip>
        ) : (
          <Grid component="span">{children}</Grid>
        )}
      </Grid>

      {/* Hidden text-measure for ellipsis/tooltip */}
      <Grid className="text-cell-measure" component="span" ref={textRef}>
        {children}
      </Grid>
    </StyledGrid>
  );
};

export default TextCell;
